import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MapPin, Timer, Bookmark, Heart, Navigation, RefreshCw } from 'lucide-react'
import { STRIPE, TYPE_META, getBiz, type PostData } from '@/data/mockData'
import { useAuthStore } from '@/store/auth.store'
import { useToggleInterest, useToggleSave } from '@/hooks/usePosts'
import { toast } from '@/components/ui/Toast'

interface Props {
  post: PostData
  initialInterested?: boolean
  initialSaved?: boolean
}

export default function PostCard({ post, initialInterested = false, initialSaved = false }: Props) {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const toggleInterest = useToggleInterest(post.id)
  const toggleSave = useToggleSave(post.id)

  // Datos del negocio: de Supabase (embebidos en el post) o del mock local
  const localBiz = getBiz(post.biz)
  const bizName = post.bizName ?? localBiz?.name ?? '—'
  const bizZone = post.bizZone ?? localBiz?.zone ?? ''
  const bizEmoji = post.bizEmoji ?? localBiz?.emoji ?? '🏪'
  const bizVerified = post.bizVerified ?? localBiz?.verified ?? false
  const bizDist = post.bizDist ?? localBiz?.dist ?? ''
  const bizCover = localBiz?.cover ?? post.img

  const tm = TYPE_META[post.type] ?? { label: post.type, color: '#0F5C5C' }

  const [interested, setInterested] = useState(initialInterested)
  const [saved, setSaved] = useState(initialSaved)

  useEffect(() => setInterested(initialInterested), [initialInterested])
  useEffect(() => setSaved(initialSaved), [initialSaved])

  const requireAuth = (action: string) => {
    if (!user) {
      toast.info(`Inicia sesión para ${action}`)
      return false
    }
    return true
  }

  const countdownText = post.endsMin
    ? `Termina en ${
        post.endsMin >= 60
          ? `${Math.floor(post.endsMin / 60)}h ${post.endsMin % 60}m`
          : `${post.endsMin} min`
      }`
    : null

  return (
    <article
      style={{
        background: '#fff',
        borderRadius: 18,
        border: '1px solid #E1E5E3',
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%',
        animation: 'pxFadeUp .4s ease',
      }}
    >
      {/* ── Header negocio ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '14px 16px 10px',
          overflow: 'hidden',
        }}
      >
        {' '}
        {/* overflow:hidden aquí también */}
        {/* Avatar */}
        <button
          onClick={() => navigate(`/biz/${post.biz}`)}
          style={{
            padding: 0,
            border: 'none',
            background: STRIPE(bizCover[0], bizCover[1]),
            cursor: 'pointer',
            width: 42,
            height: 42,
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 18 }}>{bizEmoji}</span>
        </button>
        {/* Info — min-width:0 es clave para que el texto no expanda el flex item */}
        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, overflow: 'hidden' }}>
            <button
              onClick={() => navigate(`/biz/${post.biz}`)}
              style={{
                padding: 0,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                fontWeight: 700,
                fontSize: 15,
                color: '#202523',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
                display: 'block',
              }}
            >
              {bizName}
            </button>
            {bizVerified && (
              <span style={{ fontSize: 13, color: '#0F5C5C', flexShrink: 0 }}>✓</span>
            )}
          </div>
          <div
            style={{
              fontSize: 12.5,
              color: '#6D7471',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              marginTop: 1,
              overflow: 'hidden',
            }}
          >
            <MapPin size={12} style={{ flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {bizZone}
              {bizDist ? ` · ${bizDist}` : ''}
            </span>
          </div>
        </div>
        {/* Badge tipo */}
        <div
          style={{
            background: `${tm.color}18`,
            color: tm.color,
            fontSize: 11,
            fontWeight: 700,
            padding: '5px 9px',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {post.type === 'live' ? (
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: 99,
                background: tm.color,
                animation: 'pxPulse 1.4s infinite',
              }}
            />
          ) : null}
          {tm.label}
        </div>
      </div>

      {/* ── Imagen ── */}
      <button
        onClick={() => navigate(`/post/${post.id}`)}
        style={{ display: 'block', width: '100%', padding: 0, border: 'none', cursor: 'pointer' }}
      >
        <div
          style={{
            width: '100%',
            height: 200,
            background: STRIPE(post.img[0], post.img[1]),
            position: 'relative',
          }}
        >
          {countdownText && (
            <div
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                background: 'rgba(255,255,255,.95)',
                borderRadius: 9,
                padding: '5px 9px',
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 13,
                fontWeight: 700,
                color: '#D9785B',
              }}
            >
              <Timer size={14} /> {countdownText}
            </div>
          )}
          {post.type === 'live' && (
            <div
              style={{
                position: 'absolute',
                bottom: 12,
                left: 12,
                background: 'rgba(0,0,0,.45)',
                color: '#fff',
                fontSize: 12,
                fontWeight: 600,
                padding: '5px 10px',
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <RefreshCw size={13} /> Actualizado {post.updated ?? 'hace 2 min'}
            </div>
          )}
        </div>
      </button>

      {/* ── Cuerpo ── */}
      <div style={{ padding: '14px 16px 6px', overflow: 'hidden' }}>
        <div
          style={{
            fontFamily: "'Plus Jakarta Sans',sans-serif",
            fontWeight: 700,
            fontSize: 17,
            lineHeight: 1.25,
            marginBottom: 6,
            wordBreak: 'break-word',
          }}
        >
          {post.title}
        </div>
        <div
          style={{
            fontSize: 14,
            color: '#6D7471',
            lineHeight: 1.45,
            marginBottom: 10,
            wordBreak: 'break-word',
          }}
        >
          {post.desc}
        </div>

        {/* Meta chips — flexWrap evita overflow */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 10 }}>
          {post.date && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                background: '#F1F3F2',
                borderRadius: 8,
                padding: '5px 9px',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              📅 {post.date}
              {post.start ? ` · ${post.start}` : ''}
            </span>
          )}
          {post.time && !post.date && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                background: '#F1F3F2',
                borderRadius: 8,
                padding: '5px 9px',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              🕐 {post.time}
              {post.ends ? ` hasta ${post.ends}` : ''}
            </span>
          )}
          {post.price && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                background: '#F1F3F2',
                borderRadius: 8,
                padding: '5px 9px',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              🏷️ {post.price}
            </span>
          )}
          {post.mood && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                background: '#F1F3F2',
                borderRadius: 8,
                padding: '5px 9px',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              🌡️ {post.mood}
            </span>
          )}
          {post.age && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                background: '#F1F3F2',
                borderRadius: 8,
                padding: '5px 9px',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              🛡️ {post.age}
            </span>
          )}
          {post.dur && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                background: '#F1F3F2',
                borderRadius: 8,
                padding: '5px 9px',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              ⏳ {post.dur}
            </span>
          )}
        </div>

        {/* Social proof */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <div style={{ display: 'flex', flexShrink: 0 }}>
            {['#0F5C5C', '#D9785B', '#D3A34A'].map((c, i) => (
              <div
                key={i}
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 99,
                  background: c,
                  border: '2px solid #fff',
                  marginLeft: i ? -8 : 0,
                }}
              />
            ))}
          </div>
          <span
            style={{
              fontSize: 12.5,
              color: '#6D7471',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {post.interested + (interested ? 1 : 0)} personas interesadas
          </span>
        </div>

        {post.cond && (
          <div
            style={{
              fontSize: 12,
              color: '#6D7471',
              background: '#F1F3F2',
              borderRadius: 8,
              padding: '7px 10px',
              marginBottom: 4,
              display: 'flex',
              gap: 6,
              wordBreak: 'break-word',
            }}
          >
            ℹ️ {post.cond}
          </div>
        )}
      </div>

      {/* ── Acciones ── */}
      <div style={{ display: 'flex', gap: 8, padding: '6px 16px 14px' }}>
        <button
          onClick={() => {
            if (!requireAuth('registrar interés')) return
            const next = !interested
            setInterested(next)
            toggleInterest.mutate(next, { onError: () => setInterested(!next) })
          }}
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 7,
            padding: '11px 8px',
            borderRadius: 11,
            border: `1px solid ${interested ? '#D9785B' : '#E1E5E3'}`,
            background: interested ? '#D9785B' : '#fff',
            color: interested ? '#fff' : '#202523',
            fontFamily: "'Inter',sans-serif",
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          <Heart size={17} fill={interested ? '#fff' : 'none'} style={{ flexShrink: 0 }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {interested ? 'Te interesa' : 'Me interesa'}
          </span>
        </button>

        <button
          onClick={() => toast.info('Abriendo ruta…')}
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 7,
            padding: '11px 8px',
            borderRadius: 11,
            border: 'none',
            background: '#0F5C5C',
            color: '#fff',
            fontFamily: "'Inter',sans-serif",
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          <Navigation size={17} style={{ flexShrink: 0 }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Cómo llegar
          </span>
        </button>

        <button
          onClick={() => {
            if (!requireAuth('guardar')) return
            const next = !saved
            setSaved(next)
            toggleSave.mutate(next, { onError: () => setSaved(!next) })
          }}
          style={{
            width: 46,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 11,
            border: '1px solid #E1E5E3',
            background: '#fff',
          }}
        >
          <Bookmark
            size={17}
            color={saved ? '#0F5C5C' : '#6D7471'}
            fill={saved ? '#0F5C5C' : 'none'}
          />
        </button>
      </div>
    </article>
  )
}
